<script setup lang="ts">
// ================================
// IMPORTS
// ================================
// Vue
import { useI18n } from "vue-i18n"
import { computed, inject, ref, watch } from "vue"

// Core
import { useFetchWrapper } from "@core/composables"

// Service
import type { Services } from "@services"

const { $updateMeterById } = inject<Services>("services")!

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            title: "Modifier le nom du compteur",
            button_validate: "Valider",
            button_quit: "Quitter",
            meter_name: "Nom du compteur",
            error_message:
                "Une erreur est survenue lors de la mise à jour du nom.",
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================
type Props = {
    buildingName: string | undefined
    meterId: string
}

// ================================
// PROPS & EMITS
// ================================
const props = defineProps<Props>()

const [visible] = defineModel<boolean>("visible", {
    required: true,
    default: false,
})
const emit = defineEmits(["update"])

// ================================
// COMPOSABLES
// ================================
const { isFetching, fetch, error } = useFetchWrapper(async () => {
    await $updateMeterById(props.meterId, {
        name: buildingNameModel.value,
    })
    emit("update")
    visible.value = false
})

// ================================
// REACTIVE STATE
// ================================
const buildingNameModel = ref(props.buildingName)

/**
 * Message d'erreur à afficher (provenant du fetch)
 */
const getErrorMessage = computed(() => {
    if (!error.value) return ""
    const err = error.value as Error & { detail?: { message?: string } }
    return err.detail?.message ?? err.message ?? t("error_message")
})

// ================================
// WATCHERS
// ================================
watch(
    () => props.buildingName,
    (value) => (buildingNameModel.value = value),
    {
        immediate: true,
    }
)

// ================================
// METHODS
// ================================

/**
 * Déclenche la mise à jour du nom du compteur
 */
const onSubmit = () => {
    fetch()
}
</script>

<template>
    <BaseDialog
        v-model:visible="visible"
        modal
        :closable="true"
        dismissable-mask
        class="modal-source-edit-name w-2/3 md:w-1/2 lg:w-1/3"
        :pt="{
            header: 'flex-col-reverse p-0!',
            headeractions: 'ml-auto',
            title: 'text-primary mb-4',
        }"
        :header="t('title')"
    >
        <div class="flex flex-col items-left w-full">
            <BaseInputWrapper
                input-id="meter_name"
                :label="t('meter_name')"
                unfloat
            >
                <BaseInputText
                    id="meter_name"
                    input-id="meter_name"
                    class="w-full"
                    v-model="buildingNameModel"
                />
            </BaseInputWrapper>
        </div>
        <!-- footer -->
        <template #footer>
            <div class="flex flex-col gap-2 w-full">
                <div class="flex justify-between w-full">
                    <!-- button to quit and close modal -->
                    <BaseButton
                        :label="t('button_quit')"
                        text
                        severity="secondary"
                        @click="visible = false"
                    />
                    <!-- button to quit and confirm change -->
                    <BaseButton
                        :label="t('button_validate')"
                        severity="primary"
                        autofocus
                        @click="onSubmit"
                        :loading="isFetching"
                    />
                </div>
                <BaseMessage
                    v-if="getErrorMessage"
                    severity="error"
                    variant="simple"
                    class="mt-2"
                >
                    {{ getErrorMessage }}
                </BaseMessage>
            </div>
        </template>
    </BaseDialog>
</template>
